import * as React from 'react';
import { Grid, Button } from '@mui/material';
import {
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarExport,
} from '@mui/x-data-grid';
import {
  GridCsvExportOptions,
  GridPrintExportOptions,
  GridExportDisplayOptions,
  useGridApiContext,
  GridToolbarFilterButton,
} from '@mui/x-data-grid-premium';
import { useGlobalization } from '@Translations';
import Cached from '@mui/icons-material/Cached';

import {
  StylovyzeSearchBar,
  Button as StyloButton,
  ButtonProps,
  ExportButtonWithDialogAndText,
  ExportButtonWithDialogProps,
  DataGridButton,
  DataGridButtonType,
  DropDownButton,
  ZIndex,
} from '@innovyze/stylovyze';

const HeaderContainer = ({
  children,
  justifyContent,
  container,
  gap,
  ...props
}: {
  children: React.ReactNode;
  justifyContent?: string;
  container?: boolean;
  gap?: string;
  [key: string]: unknown;
}) => (
  <Grid
    container={container}
    justifyContent={
      justifyContent as
        | 'flex-start'
        | 'flex-end'
        | 'center'
        | 'space-between'
        | 'space-around'
        | 'space-evenly'
    }
    gap={gap}
    sx={{
      padding: '16px',
      borderBottom: '1px solid rgba(224, 224, 224, 1)',
    }}
    {...props}>
    {children}
  </Grid>
);

const StyledTitle = ({
  children,
  gutterBottom,
  ...props
}: {
  children: React.ReactNode;
  gutterBottom?: boolean;
  [key: string]: unknown;
}) => (
  <div
    style={{
      fontSize: '1.25rem',
      fontWeight: 500,
      margin: gutterBottom ? '0 0 0.35em' : 0,
      lineHeight: 1.6,
    }}
    {...props}>
    {children}
  </div>
);

export interface DataGridSearchProps {
  onCancelSearch?: () => void;
  onChange?(query: string): void;
  onRequestSearch?(): void;
  placeholder?: string;
  value?: string;
  searchInProgress?: boolean;
  dataCy?: string;
  debounce?: boolean;
  handleSearchSubmit?: (value: string) => void;
}

export interface ScreenSettingsColumns {
  name: string;
  order: number;
}

export interface DataGridColumnsProps {
  // This has to be the same with the backend tableName and screenName that will be stored in DB
  tableName: string;
  screenName: string;
  defaultColumns: ScreenSettingsColumns[];
  shouldAlwaysBeVisibleColumns?: string[]; // Columns that cannot be toggled ex. actionMenu, checkbox columns
}

export interface DataGridExportProps extends GridExportDisplayOptions {
  csvOptions?: GridCsvExportOptions;
  printOptions?: GridPrintExportOptions;
  excelOptions?: GridCsvExportOptions;
}

export interface DataGridRefreshButtonProps {
  onRefresh: () => void;
}

export interface DataGridFilterButtonProps {
  filterRef?: React.RefObject<HTMLButtonElement>;
}

export interface DataGridActionsMenuProps {
  disabled?: boolean;
  menuItems: {
    title: string;
    icon?: React.ReactNode;
    disabled?: boolean;
    onClick: () => void;
  }[];
}

interface DataGridHeaderProps {
  title?: string;
  search?: DataGridSearchProps;
  columnsToolbar?: DataGridColumnsProps;
  filterToolbar?: boolean;
  filterButton?: DataGridFilterButtonProps;
  refreshButton?: DataGridRefreshButtonProps;
  exportButton?: DataGridExportProps;
  actionsMenu?: DataGridActionsMenuProps;
  toolbarContentJustifyDirection?: string;
  toolbarContainerJustifyDirection?: string;
  extraContent?: React.ReactNode;
  loading?: boolean;
  extraButtons?: {
    text: string;
    onClick: () => void;
    icon?: React.ReactNode;
    dataCy?: string;
    buttonProps?: ButtonProps;
  }[];
  exportProps?: Omit<ExportButtonWithDialogProps, 'title'> & {
    title?: string;
  };
  weave?: boolean;
  onExport?: () => void;
}

export const CustomDataGridHeader = (props: DataGridHeaderProps) => {
  const {
    title,
    search,
    columnsToolbar,
    filterToolbar,
    filterButton,
    refreshButton,
    exportButton,
    actionsMenu,
    toolbarContentJustifyDirection,
    toolbarContainerJustifyDirection,
    extraContent,
    extraButtons,
    exportProps,
    loading,
    weave,
    onExport,
  } = props;

  const { t } = useGlobalization();

  const apiRef = useGridApiContext();

  const shouldShowToolbar =
    title ??
    search ??
    columnsToolbar ??
    extraContent ??
    filterToolbar ??
    exportButton ??
    refreshButton ??
    actionsMenu;

  return shouldShowToolbar ? (
    <HeaderContainer
      justifyContent={toolbarContainerJustifyDirection || 'space-between'}
      container
      gap={'10px'}>
      {title && (
        <Grid>
          {title && (
            <StyledTitle variant="h6" gutterBottom>
              {title}
            </StyledTitle>
          )}
        </Grid>
      )}
      <Grid
        container
        justifyContent={toolbarContentJustifyDirection || 'space-between'}
        gap={'10px'}>
        <Grid justifyContent={'flex-start'} alignItems={'center'}>
          {search && (
            <StylovyzeSearchBar
              onCancelSearch={search?.onCancelSearch}
              onChange={search?.onChange}
              onRequestSearch={search?.onRequestSearch}
              placeholder={search?.placeholder}
              value={search?.value}
              style={{ minWidth: '30%' }}
              searchInProgress={search?.searchInProgress}
              dataCy={search?.dataCy}
              debounce={search?.debounce}
              handleSearchSubmit={search?.handleSearchSubmit}
            />
          )}
        </Grid>
        <Grid display="flex" justifyContent="flex-start">
          <GridToolbarContainer>
            {columnsToolbar && !weave && columnsToolbar.tableName && (
              <GridToolbarColumnsButton disabled={loading} />
            )}
            {filterToolbar && !weave && <GridToolbarFilterButton />}
            {filterToolbar && weave && (
              <DataGridButton
                sx={{ display: 'flex', gap: '8px' }}
                type={DataGridButtonType.FILTER}
                disabled={loading}
                onClick={() => {
                  apiRef.current.showFilterPanel();
                }}
                ref={filterButton?.filterRef}
              />
            )}
            {refreshButton && !weave && (
              <Button
                sx={{ display: 'flex', gap: '8px' }}
                disabled={loading}
                onClick={refreshButton.onRefresh}>
                <Cached fontSize="small" />
                <span>{t('Refresh')}</span>
              </Button>
            )}
            {refreshButton && weave && (
              <DataGridButton
                sx={{ display: 'flex', gap: '8px' }}
                type={DataGridButtonType.REFRESH}
                disabled={loading}
                onClick={refreshButton.onRefresh}
              />
            )}
            {exportButton && !weave && <GridToolbarExport {...exportButton} />}
            {exportProps && (
              <span role="presentation" onClick={() => onExport?.()}>
                <ExportButtonWithDialogAndText
                  title={exportProps?.title ?? t('Export')}
                  dialogDataCy={exportProps.dialogDataCy ?? 'export-dialog'}
                  iconDataCy={exportProps.iconDataCy ?? 'export-icon'}
                  variant={exportProps.variant ?? 'text'}
                  disabled={loading}
                  {...exportProps}
                />
              </span>
            )}
            {actionsMenu && (
              <DropDownButton
                arrow
                color="primary"
                height={36}
                options={[
                  { name: t('Actions'), cy: 'dropdown-actions' },
                  ...actionsMenu.menuItems.map(item => ({
                    name: item.title,
                    onClick: item.onClick,
                    disabled: item.disabled ?? loading,
                    ...(item.icon ? { icon: item.icon } : {}),
                  })),
                ]}
                placement="bottom-start"
                switchSelected={false}
                variant="outlined"
                fitMenuToContent
                fitButtonToContent
                disabled={actionsMenu.disabled ?? loading}
                popupZIndex={ZIndex.DropDownButtonMenu}
                buttonProps={{ style: { marginLeft: '0.5rem' } }}
              />
            )}
          </GridToolbarContainer>
          <GridToolbarContainer>
            {extraButtons &&
              extraButtons.map((button, index) => {
                const { onClick, dataCy, text, buttonProps, icon } = button;
                return (
                  <StyloButton
                    key={index}
                    onClick={onClick}
                    data-cy={dataCy}
                    startIcon={icon}
                    disabled={loading}
                    {...buttonProps}>
                    {text}
                  </StyloButton>
                );
              })}
          </GridToolbarContainer>
          <GridToolbarContainer>{extraContent}</GridToolbarContainer>
        </Grid>
      </Grid>
    </HeaderContainer>
  ) : (
    <></>
  );
};
